import { PersonDataForm } from "@/components/common/section-components/PersonDataForm";
import { AppRoutes } from "@/constants/routes";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { createPersonSchema, PersonaDetalle } from "../schema/person.schema";
import {
  getPersonaDetalle,
  updatePerson,
  UpdatePersonProps,
} from "@/services/persons.service";
import { PersonAddCancelButtons } from "@/components/common/section-components/PersonAddCancelButtons";
import { zodResolver } from "@hookform/resolvers/zod";
import { toaster } from "@/components/ui/toaster";
import {
  AuxEntity,
  getListadoEstadosCiviles,
  getListadoHobbies,
  getListadoNivelesEstudio,
  getListadoProfesiones,
  getListadoSexosBiologicos,
} from "@/services/aux-entities.service";
import { handlePermissionError } from "@/utils/permission-check";
import { useRoles } from "@/hooks/useRoles";
import { AxiosError } from "axios";

export const PersonUpdatePage = () => {
  const navigate = useNavigate();

  const [isActive, setIsActive] = useState<boolean>(false);

  const { id } = useParams();

  const methods = useForm({
    resolver: zodResolver(createPersonSchema),
  });

  const handleCancel = () => {
    navigate(AppRoutes.PERSON_DETAIL.replace(":id", String(id)));
  };
  useEffect(() => {
    setIsActive(true);
  }, []);

  const [personData, setPersonData] = useState<PersonaDetalle | null>(null);

  const [loadingPersonData, setLoadingPersonData] = useState<boolean>(false);

  const [auxEntities, setAuxEntities] = useState<{
    hobbies: AuxEntity[] | null;
    profesiones: AuxEntity[] | null;
    nivelesEstudio: AuxEntity[] | null;
    estadosCiviles: AuxEntity[] | null;
    sexosBiologicos: AuxEntity[] | null;
  }>({
    hobbies: null,
    profesiones: null,
    nivelesEstudio: null,
    estadosCiviles: null,
    sexosBiologicos: null,
  });

  const [loadingUpdatePerson, setLoadingUpdatePerson] =
    useState<boolean>(false);

  const [loadingAuxEntities, setLoadingAuxEntities] = useState<boolean>(false);

  const fetchAuxEntities = async () => {
    setLoadingAuxEntities(true);
    try {
      const [
        hobbies,
        profesiones,
        nivelesEstudio,
        estadosCiviles,
        sexosBiologicos,
      ] = await Promise.all([
        getListadoHobbies({}),
        getListadoProfesiones({}),
        getListadoNivelesEstudio(),
        getListadoEstadosCiviles(),
        getListadoSexosBiologicos(),
      ]);

      setAuxEntities({
        hobbies,
        profesiones,
        nivelesEstudio,
        estadosCiviles,
        sexosBiologicos,
      });
    } finally {
      setLoadingAuxEntities(false);
    }
  };

  const fetchPersonDetail = async (id: number) => {
    setLoadingPersonData(true);
    const response = await getPersonaDetalle({ id });

    setPersonData(response);
    setLoadingPersonData(false);
  };

  useEffect(() => {
    if (isActive && id) {
      fetchPersonDetail(Number(id));
      fetchAuxEntities();
    }
  }, [isActive, id]);

  const { fetchPermissions } = useRoles();

  const handleUpdatePerson = async () => {
    setLoadingUpdatePerson(true);
    try {
      const values = methods.getValues();

      const objToSend: UpdatePersonProps = {
        idPersona: personData?.id ?? 0,
        apellido: values?.apellido,
        apodo: values?.apodo,
        cuit: values?.cuit,
        dni: values?.dni,
        fecha: values?.fecha,
        sexoBiologico: values?.sexoBiologico?.value,
        hobbies: values?.hobbies?.map((el) => Number(el.value)) || [],
        idEstadoCivil: values?.estadoCivil?.value,
        idNivelEstudio: values?.nivelEstudio?.value,
        idProfesion: values?.profesion?.value,
        localidad: values?.localidad,
        mail: values?.mail,
        nombre: values?.nombre,
        telefono: values?.telefono,
        agro: values?.agro,
      };

      await updatePerson(objToSend);

      toaster.create({
        type: "success",
        title: "Datos personales actualizados con éxito.",
      });

      navigate(AppRoutes.PERSON_DETAIL.replace(":id", String(personData?.id)));
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
        AppRoutes.PERSON_DETAIL.replace(":id", String(personData?.id)),
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title: errorObj?.response?.data?.message ?? "No se pudieron actualizar los datos personales.",
        });
        throw error;
      }

      throw error;
    }

    setLoadingUpdatePerson(false);
  };

  const handleAdd = methods.handleSubmit(async (_data: any, _event: any) => {
    await handleUpdatePerson();
  });

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <FormProvider {...methods}>
        <PersonAddCancelButtons
          title="Editar datos personales"
          control={methods.control}
          handleAdd={() => handleAdd()}
          handleCancel={handleCancel}
          loading={loadingPersonData || loadingUpdatePerson}
          addLabel="Confirmar"
          operation="UPDATE"
        />
        {!loadingPersonData && personData && (
          <PersonDataForm
            personData={personData}
            hobbies={auxEntities.hobbies}
            profesiones={auxEntities.profesiones}
            nivelesEstudio={auxEntities.nivelesEstudio}
            estadosCiviles={auxEntities.estadosCiviles}
            sexosBiologicos={auxEntities.sexosBiologicos}
            loadingAuxEntities={loadingAuxEntities}
            operation="UPDATE"
          />
        )}
      </FormProvider>
    </Flex>
  );
};
